// Authentication Service for CMS
import CryptoJS from 'crypto-js';

class AuthService {
  constructor() {
    this.secretKey = 'droshpod-cms-secret-2024'; // Change this in production
    this.sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours
    this.maxLoginAttempts = 5;
    this.lockoutTime = 15 * 60 * 1000; // 15 minutes
  }

  // Generate secure password hash
  hashPassword(password) {
    return CryptoJS.PBKDF2(password, this.secretKey, {
      keySize: 256/32,
      iterations: 10000
    }).toString();
  }

  // Verify password
  verifyPassword(password, hash) {
    return this.hashPassword(password) === hash;
  }

  // Generate session token
  generateToken(userId) {
    const payload = {
      userId,
      timestamp: Date.now(),
      random: Math.random().toString(36)
    };
    return CryptoJS.AES.encrypt(JSON.stringify(payload), this.secretKey).toString();
  }

  // Verify session token
  verifyToken(token) {
    try {
      const decrypted = CryptoJS.AES.decrypt(token, this.secretKey).toString(CryptoJS.enc.Utf8);
      const payload = JSON.parse(decrypted);
      
      // Check if token is expired
      if (Date.now() - payload.timestamp > this.sessionTimeout) {
        return null;
      }
      
      return payload;
    } catch (error) {
      return null;
    }
  }

  // Check if user is locked out
  isLockedOut() {
    const lockoutData = localStorage.getItem('cms_lockout');
    if (!lockoutData) return false;
    
    const { attempts, timestamp } = JSON.parse(lockoutData);
    if (Date.now() - timestamp > this.lockoutTime) {
      localStorage.removeItem('cms_lockout');
      return false;
    }
    
    return attempts >= this.maxLoginAttempts;
  }

  // Record failed login attempt
  recordFailedAttempt() {
    const lockoutData = localStorage.getItem('cms_lockout');
    let attempts = 1;
    let timestamp = Date.now();
    
    if (lockoutData) {
      const data = JSON.parse(lockoutData);
      if (Date.now() - data.timestamp < this.lockoutTime) {
        attempts = data.attempts + 1;
        timestamp = data.timestamp;
      }
    }
    
    localStorage.setItem('cms_lockout', JSON.stringify({ attempts, timestamp }));
    
    return {
      attempts,
      remaining: this.maxLoginAttempts - attempts,
      lockedOut: attempts >= this.maxLoginAttempts
    };
  }

  // Clear failed attempts
  clearFailedAttempts() {
    localStorage.removeItem('cms_lockout');
  }

  // Login user
  async login(username, password) {
    // Check if locked out
    if (this.isLockedOut()) {
      return {
        success: false,
        message: 'Account temporarily locked due to too many failed attempts. Please try again later.',
        lockedOut: true
      };
    }

    // Get stored admin credentials
    const storedAdmin = localStorage.getItem('cms_admin_credentials');
    if (!storedAdmin) {
      // Set default admin if none exists
      const defaultAdmin = {
        username: 'admin',
        password: this.hashPassword('droshpod2024'),
        role: 'super_admin',
        createdAt: Date.now()
      };
      localStorage.setItem('cms_admin_credentials', JSON.stringify(defaultAdmin));
    }

    const adminData = JSON.parse(localStorage.getItem('cms_admin_credentials'));
    
    // Verify credentials
    if (username === adminData.username && this.verifyPassword(password, adminData.password)) {
      // Clear failed attempts
      this.clearFailedAttempts();
      
      // Generate session token
      const token = this.generateToken(adminData.username);
      
      // Store session
      const session = {
        token,
        user: {
          username: adminData.username,
          role: adminData.role,
          loginTime: Date.now()
        }
      };
      
      localStorage.setItem('cms_session', JSON.stringify(session));
      
      return {
        success: true,
        message: 'Login successful',
        user: session.user,
        token
      };
    } else {
      // Record failed attempt
      const attemptData = this.recordFailedAttempt();
      
      return {
        success: false,
        message: `Invalid credentials. ${attemptData.remaining} attempts remaining.`,
        attemptsRemaining: attemptData.remaining,
        lockedOut: attemptData.lockedOut
      };
    }
  }

  // Logout user
  logout() {
    localStorage.removeItem('cms_session');
    return { success: true, message: 'Logged out successfully' };
  }

  // Check if user is authenticated
  isAuthenticated() {
    const session = localStorage.getItem('cms_session');
    if (!session) return false;
    
    try {
      const sessionData = JSON.parse(session);
      const payload = this.verifyToken(sessionData.token);
      
      if (!payload) {
        localStorage.removeItem('cms_session');
        return false;
      }
      
      return true;
    } catch (error) {
      localStorage.removeItem('cms_session');
      return false;
    }
  }

  // Get current user
  getCurrentUser() {
    const session = localStorage.getItem('cms_session');
    if (!session) return null;
    
    try {
      const sessionData = JSON.parse(session);
      const payload = this.verifyToken(sessionData.token);
      
      if (!payload) {
        localStorage.removeItem('cms_session');
        return null;
      }
      
      return sessionData.user;
    } catch (error) {
      localStorage.removeItem('cms_session');
      return null;
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    const adminData = JSON.parse(localStorage.getItem('cms_admin_credentials'));
    
    if (!this.verifyPassword(currentPassword, adminData.password)) {
      return {
        success: false,
        message: 'Current password is incorrect'
      };
    }
    
    // Update password
    adminData.password = this.hashPassword(newPassword);
    adminData.updatedAt = Date.now();
    
    localStorage.setItem('cms_admin_credentials', JSON.stringify(adminData));
    
    return {
      success: true,
      message: 'Password changed successfully'
    };
  }

  // Update admin profile
  updateProfile(profileData) {
    const adminData = JSON.parse(localStorage.getItem('cms_admin_credentials'));
    
    adminData.username = profileData.username || adminData.username;
    adminData.email = profileData.email || adminData.email;
    adminData.updatedAt = Date.now();
    
    localStorage.setItem('cms_admin_credentials', JSON.stringify(adminData));
    
    return {
      success: true,
      message: 'Profile updated successfully'
    };
  }

  // Get admin profile
  getProfile() {
    const adminData = JSON.parse(localStorage.getItem('cms_admin_credentials'));
    if (!adminData) return null;
    
    return {
      username: adminData.username,
      email: adminData.email,
      role: adminData.role,
      createdAt: adminData.createdAt,
      updatedAt: adminData.updatedAt
    };
  }

  // Check permissions
  hasPermission(permission) {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Super admin has all permissions
    if (user.role === 'super_admin') return true;
    
    // Define role permissions
    const rolePermissions = {
      admin: ['read', 'write', 'edit', 'delete'],
      editor: ['read', 'write', 'edit'],
      viewer: ['read']
    };
    
    return rolePermissions[user.role]?.includes(permission) || false;
  }

  // Session management
  extendSession() {
    const session = localStorage.getItem('cms_session');
    if (!session) return false;
    
    try {
      const sessionData = JSON.parse(session);
      const payload = this.verifyToken(sessionData.token);
      
      if (!payload) return false;
      
      // Generate new token with extended time
      const newToken = this.generateToken(payload.userId);
      sessionData.token = newToken;
      
      localStorage.setItem('cms_session', JSON.stringify(sessionData));
      return true;
    } catch (error) {
      return false;
    }
  }

  // Get session info
  getSessionInfo() {
    const session = localStorage.getItem('cms_session');
    if (!session) return null;
    
    try {
      const sessionData = JSON.parse(session);
      const payload = this.verifyToken(sessionData.token);
      
      if (!payload) return null;
      
      return {
        user: sessionData.user,
        loginTime: sessionData.user.loginTime,
        expiresAt: sessionData.user.loginTime + this.sessionTimeout,
        timeRemaining: (sessionData.user.loginTime + this.sessionTimeout) - Date.now()
      };
    } catch (error) {
      return null;
    }
  }
}

export default new AuthService();
